// frontend/src/components/public/blocks/TeamBlock.tsx
import React from 'react';
import { Box, Typography, Grid, Card, CardContent, Avatar, IconButton } from '@mui/material';
import { LinkedIn as LinkedInIcon } from '@mui/icons-material';

interface TeamBlockProps {
  data: {
    members: Array<{
      name: string;
      role: string;
      bio: string;
      image?: string;
      credentials?: string[];
      linkedin?: string;
    }>;
    advisors?: Array<{
      name: string;
      expertise: string;
      bio?: string;
      image?: string;
    }>;
    layout: 'grid' | 'list';
  };
  theme?: {
    primaryColor: string;
    accentColor: string;
  };
}

const TeamBlock: React.FC<TeamBlockProps> = ({ data, theme }) => {
  const { members = [], advisors = [], layout = 'grid' } = data;
  const accentColor = theme?.accentColor || '#FF0000';
  const primaryColor = theme?.primaryColor || '#000000';

  if (members.length === 0) {
    return null;
  }

  // Grid Layout
  if (layout === 'grid') {
    return (
      <Box
        id="team"
        sx={{
          py: { xs: 6, md: 10 },
          px: { xs: 3, md: 6 },
          maxWidth: '1200px',
          mx: 'auto'
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            fontWeight: 700,
            color: primaryColor,
            mb: 6,
            textAlign: 'center'
          }}
        >
          Our Team
        </Typography>

        <Grid container spacing={4}>
          {members.map((member, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 32px ${accentColor}22`,
                    borderColor: accentColor
                  }
                }}
              >
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <Avatar
                    src={member.image}
                    sx={{
                      width: 120,
                      height: 120,
                      mx: 'auto',
                      mb: 2,
                      border: `3px solid ${accentColor}`
                    }}
                  >
                    {member.name.charAt(0)}
                  </Avatar>

                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: '1.125rem',
                      fontWeight: 700,
                      color: primaryColor,
                      mb: 0.5
                    }}
                  >
                    {member.name}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.95rem',
                      color: accentColor,
                      fontWeight: 600,
                      mb: 2
                    }}
                  >
                    {member.role}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.875rem',
                      lineHeight: 1.6,
                      color: 'text.secondary',
                      mb: 2
                    }}
                  >
                    {member.bio}
                  </Typography>

                  {member.credentials && member.credentials.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      {member.credentials.map((credential, idx) => (
                        <Typography
                          key={idx}
                          variant="caption"
                          sx={{
                            display: 'block',
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                            fontStyle: 'italic'
                          }}
                        >
                          {credential}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  {member.linkedin && (
                    <IconButton
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: accentColor }}
                    >
                      <LinkedInIcon />
                    </IconButton>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

 {advisors.length > 0 && (
  <Box sx={{ mt: 8 }}>
    <Typography
      variant="h4"
      sx={{
        fontSize: { xs: '1.5rem', md: '1.875rem' },
        fontWeight: 700,
        color: primaryColor,
        mb: 4,
        textAlign: 'center'
      }}
    >
      Advisors
    </Typography>

    <Grid container spacing={3}>
      {advisors.map((advisor, index) => (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <Card
            elevation={0}
            sx={{
              height: '100%',
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 8px 24px ${accentColor}22`,
                borderColor: accentColor
              }
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              {advisor.image && (
                <Avatar
                  src={advisor.image}
                  sx={{
                    width: 100,
                    height: 100,
                    mx: 'auto',
                    mb: 2,
                    border: `2px solid ${accentColor}`
                  }}
                >
                  {advisor.name.charAt(0)}
                </Avatar>
              )}
              
              <Typography
                variant="h6"
                sx={{
                  fontSize: '1rem',
                  fontWeight: 700,
                  color: primaryColor,
                  mb: 0.5
                }}
              >
                {advisor.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.875rem',
                  color: accentColor,
                  fontWeight: 600,
                  mb: 1
                }}
              >
                {advisor.expertise}
              </Typography>
              {advisor.bio && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.8rem',
                    color: 'text.secondary',
                    lineHeight: 1.5
                  }}
                >
                  {advisor.bio}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  </Box>
)}
      </Box>
    );
  }

  // List Layout
  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        px: { xs: 3, md: 6 },
        maxWidth: '900px',
        mx: 'auto'
      }}
    >
      <Typography
        variant="h3"
        sx={{
          fontSize: { xs: '1.75rem', md: '2.25rem' },
          fontWeight: 700,
          color: primaryColor,
          mb: 6,
          textAlign: 'center'
        }}
      >
        Our Team
      </Typography>

      {members.map((member, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            gap: 3,
            mb: 5,
            pb: 5,
            borderBottom: index < members.length - 1 ? '1px solid #e0e0e0' : 'none',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'center', sm: 'flex-start' }
          }}
        >
          <Avatar
            src={member.image}
            sx={{
              width: 120,
              height: 120,
              border: `3px solid ${accentColor}`,
              flexShrink: 0
            }}
          >
            {member.name.charAt(0)}
          </Avatar>

          <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
            <Typography
              variant="h5"
              sx={{
                fontSize: '1.25rem',
                fontWeight: 700,
                color: primaryColor,
                mb: 0.5
              }}
            >
              {member.name}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: '1rem',
                color: accentColor,
                fontWeight: 600,
                mb: 2
              }}
            >
              {member.role}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                fontSize: '0.95rem',
                lineHeight: 1.7,
                color: 'text.secondary',
                mb: 2
              }}
            >
              {member.bio}
            </Typography>

            {member.credentials && member.credentials.length > 0 && (
              <Box sx={{ mb: 2 }}>
                {member.credentials.map((credential, idx) => (
                  <Typography
                    key={idx}
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontSize: '0.8rem',
                      color: 'text.secondary',
                      fontStyle: 'italic'
                    }}
                  >
                    {credential}
                  </Typography>
                ))}
              </Box>
            )}

            {member.linkedin && (
              <IconButton
                href={member.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: accentColor }}
              >
                <LinkedInIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default TeamBlock;